import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableListItem } from "./SortableListItem";

export interface SortableListHolderProps {
  canMove?: boolean;
  handleDragEnd: (event: DragEndEvent) => void;
  items: { id: string; element: React.ReactElement }[];
  maxHeights?: (number | undefined)[];
  showBorder?: boolean;
}

/**
 * Container for drag-and-drop sortable list items.
 *
 * @param props - The component props
 * @param props.canMove - Optional. When true, enables drag-and-drop reordering
 * @param props.handleDragEnd - Callback invoked when a drag operation completes
 * @param props.items - Array of items with unique IDs and elements to display
 * @param props.maxHeights - Optional array of maximum heights for each item
 * @param props.showBorder - Optional. When true, displays border around each item (default: true)
 */
export const SortableListHolder = ({
  canMove,
  handleDragEnd,
  items,
  maxHeights,
  showBorder = true,
}: SortableListHolderProps): React.ReactElement => {
  // Set up sortable list
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToVerticalAxis]}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <ol>
        <SortableContext
          strategy={verticalListSortingStrategy}
          items={items}
        >
          {items.map((t, ix) => (
            <SortableListItem
              key={t.id}
              id={t.id}
              canMove={canMove}
              maxHeight={maxHeights ? maxHeights[ix] : undefined}
              showBorder={showBorder}
            >
              {t.element}
            </SortableListItem>
          ))}
        </SortableContext>
      </ol>
    </DndContext>
  );
};

SortableListHolder.displayName = "SortableListHolder";
